import { useCallback, useMemo, useRef, useState } from 'react';

import type { StepUpTwoFactorModalProps } from '../pages/Team/components/StepUpTwoFactorModal';
import { AppExperiments, useIsTreatment } from '../utils/experiments';
import { isStepUpProofTokenError } from '../utils/stepUpProofToken';

// The legitimate path needs exactly one recovery (submit, rejected, challenge,
// resubmit); beyond that rejections are systematic and re-opening the challenge
// traps the user.
const MAX_STEP_UP_RECOVERY_ATTEMPTS = 1;

type StepUpSubmitHandler = (actionProofToken?: string) => void | Promise<void>;

type UseStepUpSubmissionParams = {
  isGatedRoleMutation: boolean;
};

type StepUpModalProps = Pick<StepUpTwoFactorModalProps, 'visible' | 'onSuccess' | 'onCancel'>;

type UseStepUpSubmissionReturn = {
  // onSubmit is per call, not per render: useEditSubmission and
  // useInviteSubmission build it from handleStepUpProofTokenError, so it does not
  // exist at the useStepUpSubmission call site. The latest one is reused for the
  // resubmit after a challenge.
  submit: (onSubmit: StepUpSubmitHandler) => void;
  // Spread onto StepUpTwoFactorModal.
  stepUpModalProps: StepUpModalProps;
  isChallengeVisible: boolean;
  // submit() already resets the counter; flows call this again on success.
  resetStepUpRecovery: () => void;
  // Returns true when it re-opened the challenge, so the flow skips its error UI.
  handleStepUpProofTokenError: (error: unknown) => boolean;
};

/**
 * Drives one member-management mutation through the step-up challenge. The
 * proof token goes straight from StepUpTwoFactor's onSuccess into onSubmit and
 * is never stored, so a rejected token cannot be replayed on the retry.
 *
 * The proactive challenge is gated on the Cifer experiment t0018_c2608c57. The
 * backend governs enforcement (in part by accessing
 * 2026.jul.acc.workspace_member_management_step_up). If the BE requires a proof
 * token for an action, but the FE does not provide one, then
 * handleStepUpProofTokenError will trigger the 2fa flow. This way, if FE forgets
 * to do 2FA, it is not a dead end.
 */
export function useStepUpSubmission({
  isGatedRoleMutation,
}: UseStepUpSubmissionParams): UseStepUpSubmissionReturn {
  const isStepUpEnabled = useIsTreatment(AppExperiments.t0018_c2608c57);
  const [isChallengeVisible, setIsChallengeVisible] = useState(false);
  const recoveryAttempts = useRef(0);
  const onSubmitRef = useRef<StepUpSubmitHandler>();

  const hideChallenge = useCallback(() => setIsChallengeVisible(false), []);
  const resetStepUpRecovery = useCallback(() => {
    recoveryAttempts.current = 0;
  }, []);

  const requiresStepUp = isStepUpEnabled && isGatedRoleMutation;

  const submit = useCallback(
    (onSubmit: StepUpSubmitHandler) => {
      onSubmitRef.current = onSubmit;
      resetStepUpRecovery();

      if (requiresStepUp) {
        setIsChallengeVisible(true);
        return;
      }

      void onSubmit();
    },
    [requiresStepUp, resetStepUpRecovery],
  );

  const handleStepUpSuccess = useCallback(
    (proofToken: string) => {
      hideChallenge();
      void onSubmitRef.current?.(proofToken);
    },
    [hideChallenge],
  );

  const handleStepUpProofTokenError = useCallback((error: unknown) => {
    if (!isStepUpProofTokenError(error)) {
      return false;
    }

    if (recoveryAttempts.current >= MAX_STEP_UP_RECOVERY_ATTEMPTS) {
      return false;
    }

    recoveryAttempts.current += 1;
    setIsChallengeVisible(true);
    return true;
  }, []);

  const stepUpModalProps = useMemo(
    () => ({
      visible: isChallengeVisible,
      onSuccess: handleStepUpSuccess,
      onCancel: hideChallenge,
    }),
    [handleStepUpSuccess, hideChallenge, isChallengeVisible],
  );

  return {
    submit,
    stepUpModalProps,
    isChallengeVisible,
    resetStepUpRecovery,
    handleStepUpProofTokenError,
  };
}
